
import { useState, useEffect } from 'react'
import { Check, X, Shield, Zap, Building, Crown, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'

interface Plan {
    id: string
    name: string
    price_cents: number
    billing_interval: string
    features: {
        analysis_limit: number
        public_access: boolean
    }
}

interface Subscription {
    id: string
    plan_id: string
    plan_name: string
    status: string
    features: any
}

interface Usage {
    allowed: boolean
    limit: number
    used: number
    plan_name: string
    extra_limit?: number
}

export function PlansPage() {
    const { user } = useAuth()
    const { toast } = useToast()
    const [plans, setPlans] = useState<Plan[]>([])
    const [currentSub, setCurrentSub] = useState<Subscription | null>(null)
    const [usage, setUsage] = useState<Usage | null>(null)
    const [loading, setLoading] = useState(true)
    const [processing, setProcessing] = useState(false)

    useEffect(() => {
        const fetchData = async () => {
            if (!user) return
            try {
                const token = await user.getIdToken()

                // Fetch Plans
                const plansRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/subscription/plans`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                const plansData = await plansRes.json()
                setPlans(plansData.data || [])

                // Fetch Current Sub
                const subRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/subscription/current`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                if (subRes.ok) {
                    const subData = await subRes.json()
                    setCurrentSub(subData.subscription)
                    setUsage(subData.usage)
                }
            } catch (error) {
                console.error("Failed to fetch plans data", error)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [user])

    const handleUpgrade = async (planName: string) => {
        if (!user) return
        setProcessing(true)
        try {
            const token = await user.getIdToken()
            
            // Check if plan is free
            const planDetails = plans.find(p => p.name === planName);
            if (planDetails && Number(planDetails.price_cents) === 0) {
                const upgradeRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/subscription/upgrade`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ plan_name: planName })
                });
                
                if (!upgradeRes.ok) throw new Error('Failed to switch to free plan');
                
                toast({
                    title: "Plan Updated",
                    description: `You are now on the ${planName} plan.`,
                });
                window.location.reload();
                return;
            }

            // 1. Create order
            const orderRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/payment/create-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ plan_name: planName })
            })

            if (!orderRes.ok) throw new Error('Failed to create payment order')
            
            const orderData = await orderRes.json()
            
            // 2. Open Razorpay Checkout
            const options = {
                key: import.meta.env.VITE_RAZORPAY_KEY_ID,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Uniscout",
                description: `Upgrade to ${planName} Plan`,
                order_id: orderData.order_id,
                handler: async function (response: any) {
                    try {
                        // 3. Verify Payment
                        const verifyRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/payment/verify`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ 
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_signature: response.razorpay_signature,
                                plan_name: planName
                            })
                        })
                        
                        if (!verifyRes.ok) throw new Error('Payment verification failed')
                        
                        toast({
                            title: "Upgrade Successful",
                            description: `You are now on the ${planName} plan.`,
                        })
                        window.location.reload()
                    } catch (err) {
                        toast({
                            title: "Upgrade Failed",
                            description: "Payment verified but upgrade failed. Please contact support.",
                            variant: "destructive"
                        })
                    }
                },
                prefill: {
                    email: user.email
                },
                theme: {
                    color: "#3399cc"
                }
            };
            
            const rzp = new (window as any).Razorpay(options);
            rzp.on('payment.failed', function (response: any){
                toast({
                    title: "Payment Failed",
                    description: response.error.description,
                    variant: "destructive"
                })
            });
            rzp.open();

        } catch (error: any) {
            toast({
                title: "Upgrade Failed",
                description: error.message || "Could not process upgrade. Please try again.",
                variant: "destructive"
            })
        } finally {
            setProcessing(false)
        }
    }

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    })

    const getIcon = (name: string) => {
        switch (name.toLowerCase()) {
            case 'explorer': return <Shield className="w-6 h-6 text-blue-500" />
            case 'basic': return <Zap className="w-6 h-6 text-purple-500" />
            case 'premium': return <Crown className="w-6 h-6 text-yellow-500" />
            default: return <Shield className="w-6 h-6" />
        }
    }

    if (loading) return <div className="p-8 text-center">Loading plans...</div>

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 md:p-12">
            <div className="max-w-7xl mx-auto space-y-8">
                <div className="text-center space-y-4">
                    <h1 className="text-4xl font-bold text-slate-900 dark:text-white">Subscription Plans</h1>
                    <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                        Scale your investment analysis with the right plan. Upgrade anytime.
                    </p>

                    {usage && (
                        <div className="inline-flex items-center px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-full shadow-sm text-sm">
                            <span className="font-medium text-slate-600 dark:text-slate-400 mr-2">Current Usage:</span>
                            <span className={`font-bold ${usage.allowed ? 'text-green-600' : 'text-red-500'}`}>
                                {usage.used} / {usage.limit === -1 ? '∞' : usage.limit} Analyses
                            </span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {plans.sort((a, b) => {
                        const limitA = a.features.analysis_limit === -1 ? Infinity : a.features.analysis_limit
                        const limitB = b.features.analysis_limit === -1 ? Infinity : b.features.analysis_limit
                        return limitA - limitB
                    }).map((plan) => {
                        const isCurrent = currentSub?.plan_name === plan.name
                        const isPaid = plan.price_cents > 0
                        const isEnterprise = plan.name.toLowerCase() === 'enterprise'

                        const currentLimit = usage?.limit === -1 ? Infinity : (usage?.limit || 0)
                        const thisPlanLimit = plan.features.analysis_limit === -1 ? Infinity : plan.features.analysis_limit
                        const isLowerOrEqualPlan = thisPlanLimit <= currentLimit
                        const hasUsageRemaining = usage?.allowed
                        const isCurrentAndActive = isCurrent && hasUsageRemaining
                        const shouldDisableDowngrade = !isCurrent && isLowerOrEqualPlan && hasUsageRemaining

                        return (
                            <Card key={plan.id} className={`relative flex flex-col h-full border-2 ${isCurrentAndActive ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-slate-200 dark:border-slate-800'}`}>
                                {isCurrentAndActive && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <Badge className="bg-blue-500 hover:bg-blue-600">Current Plan</Badge>
                                    </div>
                                )}

                                <CardHeader>
                                    <div className="mb-4">{getIcon(plan.name)}</div>
                                    <CardTitle>{plan.name}</CardTitle>
                                    <CardDescription>
                                        <span className="text-2xl font-bold text-slate-900 dark:text-white">
                                            {isEnterprise ? 'Custom' : (isPaid ? formatter.format(plan.price_cents / 100) : 'Free')}
                                        </span>
                                    </CardDescription>
                                </CardHeader>

                                <CardContent className="flex-grow space-y-4">
                                    <ul className="space-y-2 text-sm">
                                        <li className="flex items-center">
                                            <Check className="w-4 h-4 text-green-500 mr-2" />
                                            <span>
                                                {plan.features.analysis_limit === -1
                                                    ? 'Unlimited Analysis'
                                                    : `${plan.features.analysis_limit} Analysis${plan.features.analysis_limit === 1 ? '' : '/mo'}`
                                                }
                                            </span>
                                        </li>
                                        <li className="flex items-center">
                                            {plan.features.public_access ? (
                                                <Check className="w-4 h-4 text-green-500 mr-2" />
                                            ) : (
                                                <X className="w-4 h-4 text-slate-400 mr-2" />
                                            )}
                                            <span className={plan.features.public_access ? '' : 'text-slate-500 line-through'}>
                                                Public Deal Flow
                                            </span>
                                        </li>
                                        <li className="flex items-center">
                                            <Check className="w-4 h-4 text-green-500 mr-2" />
                                            <span>Email & Web Upload</span>
                                        </li>
                                    </ul>
                                </CardContent>

                                <CardFooter>
                                    {isEnterprise ? (
                                        <a href="mailto:help@uniscout.ai" className="w-full">
                                            <Button className="w-full" variant="outline">
                                                Contact Us
                                            </Button>
                                        </a>
                                    ) : (
                                        <Button
                                            className="w-full"
                                            variant={isCurrentAndActive || shouldDisableDowngrade ? "outline" : "default"}
                                            disabled={isCurrentAndActive || shouldDisableDowngrade || processing}
                                            onClick={() => handleUpgrade(plan.name)}
                                        >
                                            {isCurrentAndActive ? "Active" : (shouldDisableDowngrade ? "Already have higher plan" : (isCurrent ? "Repurchase" : "Upgrade"))}
                                        </Button>
                                    )}
                                </CardFooter>
                            </Card>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
